import { type ReactNode, createContext, useContext } from "react";
import { type UseCollectionOptions, useCollection } from "../hooks/use-collection";
import { SearchInput, type SearchInputProps } from "./search-input";

interface CollectionContextValue<T> {
  keyword: string;
  setKeyword: (value: string) => void;
  filteredData: T[];
}

const CollectionContext = createContext<CollectionContextValue<unknown> | null>(null);

interface CollectionProps<T> extends UseCollectionOptions<T> {
  children: ReactNode;
}

function Collection<T>({ data, filter, children }: CollectionProps<T>) {
  const { keyword, setKeyword, filteredData } = useCollection({
    data,
    filter,
  });

  return (
    <CollectionContext.Provider
      value={{
        keyword,
        setKeyword,
        filteredData,
      }}
    >
      {children}
    </CollectionContext.Provider>
  );
}

function CollectionSearchInput(props: Omit<SearchInputProps, "value" | "onChange" | "onReset">) {
  const context = useContext(CollectionContext);

  if (!context) {
    throw new Error("CollectionSearchInput must be used within Collection");
  }

  return (
    <SearchInput
      value={context.keyword}
      onChange={(e) => context.setKeyword(e.target.value)}
      onReset={() => context.setKeyword("")}
      {...props}
    />
  );
}

interface CollectionResultProps<T> {
  children: (props: { keyword: string; collections: T[] }) => ReactNode;
}

function CollectionResult<T>({ children }: CollectionResultProps<T>) {
  const context = useContext(CollectionContext);
  if (!context) {
    throw new Error("CollectionResult must be used within Collection");
  }

  return children({
    keyword: context.keyword,
    collections: context.filteredData as T[],
  });
}

Collection.SearchInput = CollectionSearchInput;
Collection.Result = CollectionResult;

export { Collection };
